import { memo, useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { Slider } from "../../../../components/Slider/Slider";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../../../components/Loader/Loader";
import { closeLiteTradePositionAction } from "../../../../modules/LiteTrade/liteTradeActions";
import {
    getLiteTradeAccountMetrics,
    getLiteTradeAssets,
    makeSelectorGetLiteTradeAccountPositionInfo
} from "../../../../modules/LiteTrade/liteTradeSelectors";
import { LITE_TRADE_POSITION_SIDE } from "../../../../modules/LiteTrade/liteTradeConstants";
import BinIcon from "../../../../components/icon/Icon";
import IconAsset from "../../../../components/icon/IconAsset";
import _ from "lodash";
import {
    SlideMobileAssetInfoStyled,
    SlideMobileAssetInfoSymbolStyled,
    SlideMobileModalBoxStyled,
    SlideMobileModalHeaderStyled,
    SlideMobileModalInfoRowStyled
} from "./styled/SlideMobileStyled";
import { IMobileSliderPageId, MOBILE_SLIDER_PAGES_IDS } from "../mobileSliderPagesConstants";
import { openMobileSliderPageAction } from "../../../mobileAppActions";
import {
    makeSelectorMobilePositionUpdateProtectionOrderInfo
} from "../../MobilePosition/MobilePositionUpdate/mobilePositionUpdateSelectors";
import { ORDER_TYPES } from "../../../../components/OrderTypeSelector/orderTypeSelectorConstants";
import LiteTradePortfolioPositionInfo
    from "../../../../modules/LiteTrade/components/LiteTradePortfolio/LiteTradePortfolioPositionInfo";
import type { IClosePositionActionPayload } from "../../../../modules/LiteTrade/types/liteTradeActions.t";
import type { IOpenMobileSliderPageAction } from "../../../types/mobileAppActions.t";
import type Asset from "../../../../modules/Asset/Asset";
import type {
    ILiteTradeAsset,
    ILiteTradeAssetSymbol,
} from "../../../../modules/LiteTrade/types/liteTradeReducer.t";
import MobilePositionInactiveWrapper from "../../MobilePosition/components/MobilePositionInactiveWrapper";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";
import { IAccountCode } from "../../../../app/types/appReducer.t";
import SlideMobilePositionModalMarket from "./SlideMobilePositionModalMarket";
import { getAppFeatures } from "../../../../app/appSelectors";
import { useAbTestOption } from "../../../../modules/AbTests/abTestsHooks";
import { AB_TESTS_NAMES, AB_TEST_OPEN_ORDER_V2_OPTIONS } from "../../../../modules/AbTests/abTestsConstants";
import { FEATURES } from "../../../../app/appConstants";
import { useLocation } from "react-router";
import { useMakeSelector } from "../../../../hooks/useMakeSelector";
import { useMobilePositionPL } from "../../MobilePosition/hooks/useMobilePositionPL";
import { SlideMobilePositionModalLastContainerStyled } from "./styled/SlideMobilePositionModalStyled";
import { locationSlice } from '../../../../modules/Location/locationReducer';
import { LOCATION_NAMES } from '../../../../modules/Location/locationConstants';


interface ISlideMobilePositionModalProps {
    orderId?: string,
    symbol: ILiteTradeAssetSymbol,
    positionCode: string,
    assetInstance: Asset,
    openAssetOnClick?: boolean,
    onClose: (action: string) => void,
    accountCode?: IAccountCode,
}

const SlideMobilePositionModal = (props: ISlideMobilePositionModalProps) => {
    const {
        orderId,
        symbol,
        positionCode,
        assetInstance,
        openAssetOnClick,
        onClose,
    } = props;
    const location = useLocation();
    const features = useSelector(getAppFeatures);
    const openOrderV2AbTestOption = useAbTestOption(AB_TESTS_NAMES.OPEN_ORDER_V2);
    const isOpenOrderV2AbTestOptionActive = openOrderV2AbTestOption !== undefined;
    const dispatch = useDispatch();
    const { getLangPath } = useLangContext();
    const closePosition = useCallback((payload: IClosePositionActionPayload) => dispatch(closeLiteTradePositionAction(payload)), [dispatch]);
    const openMobileSliderPage = useCallback((payload: IOpenMobileSliderPageAction) => dispatch(openMobileSliderPageAction(payload)), [dispatch]);
    const assets = useSelector(getLiteTradeAssets);
    const positionInfo = useMakeSelector(makeSelectorGetLiteTradeAccountPositionInfo, [positionCode]);
    const orderStopLossProps = useMemo(() => ({
        orderId,
        positionCode,
        protectionOrderType: ORDER_TYPES.STOP,
    }), [orderId, positionCode]);
    const orderStopLoss = useMakeSelector(makeSelectorMobilePositionUpdateProtectionOrderInfo, [orderStopLossProps]);
    const orderTakeProfitProps = useMemo(() => ({
        orderId,
        positionCode,
        protectionOrderType: ORDER_TYPES.LIMIT,
    }), [orderId, positionCode]);
    const orderTakeProfit = useMakeSelector(makeSelectorMobilePositionUpdateProtectionOrderInfo, [orderTakeProfitProps]);
    const [closingPosition, setClosingPosition] = useState(false);
    const [open, setOpen] = useState<'main' | 'last_position'>('main');
    const onTopUpClickHandler = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({name: LOCATION_NAMES.DEPOSIT}))
    }, [dispatch]);
    const metrics = useSelector(getLiteTradeAccountMetrics);
    const isLastPosition = metrics?.positions?.length === 1;
    const pnl = useMobilePositionPL(positionInfo, assetInstance);
    const balanceAfterClose = metrics?.balance + pnl;

    const closePositionHandler = useCallback((item) => {
        if (isLastPosition && balanceAfterClose < 0 && open === 'main') {
            setOpen('last_position');
        } else {
            setClosingPosition(true);
            if (positionInfo)
                closePosition({
                    positionCode: positionInfo.positionCode,
                    quantity: positionInfo.quantity,
                    side: positionInfo.side === LITE_TRADE_POSITION_SIDE.SELL ? LITE_TRADE_POSITION_SIDE.BUY : LITE_TRADE_POSITION_SIDE.SELL,
                    symbol: positionInfo.symbol,
                    onSuccessCallback: () => onClose('close_position'),
                });
        }
    }, [balanceAfterClose, closePosition, isLastPosition, onClose, open, positionInfo]);

    const editPositionHandler = useCallback(() => {
        if (
            features[FEATURES.CREATE_ORDER_2] &&
            (!isOpenOrderV2AbTestOptionActive || openOrderV2AbTestOption === AB_TEST_OPEN_ORDER_V2_OPTIONS.ENABLED)
        ) {
            const searchParams = new URLSearchParams(location.search);
            const name = searchParams.getAll('s') as IMobileSliderPageId[];
            name.push(MOBILE_SLIDER_PAGES_IDS.POSITION_UPDATE_2);
            openMobileSliderPage({
                name,
                'position-code': positionInfo?.positionCode,
            });
            onClose('edit_position');
        } else {
            openMobileSliderPage({
                name: MOBILE_SLIDER_PAGES_IDS.POSITION_UPDATE,
                'position-code': positionInfo?.positionCode,
            });
            onClose('edit_position');
        }

    }, [features, isOpenOrderV2AbTestOptionActive, location.search, onClose, openMobileSliderPage, openOrderV2AbTestOption, positionInfo?.positionCode]);

    const asset = useMemo<ILiteTradeAsset | undefined>(() => _.find(assets, { symbol: positionInfo?.symbol }) as ILiteTradeAsset, [assets, positionInfo?.symbol]);

    const clickAssetHandler = useCallback((e) => {
        e.preventDefault();
        if (openAssetOnClick) {
            dispatch(locationSlice.actions.openLocationAction({
                name: LOCATION_NAMES.ASSET,
                searchParams: {
                    symbol: positionInfo?.symbol,
                },
            }));
        }
        onClose('asset');
    }, [openAssetOnClick, onClose, dispatch, positionInfo]);

    const clickAssetScheduleHandler = useCallback((e) => {
        e.preventDefault();
        if (openAssetOnClick) {
            dispatch(locationSlice.actions.openLocationAction({
                name: LOCATION_NAMES.ASSET,
                searchParams: {
                    symbol: positionInfo?.symbol,
                    tab: 'schedule'
                },
            }));
        }
        onClose('asset_schedule');
    }, [openAssetOnClick, onClose, dispatch, positionInfo]);

    const timeToBidding: number = assetInstance.timeBeforeBidding();

    const handleCloseCommon = useCallback(() => {
        onClose('close');
    }, [onClose]);

    if (!positionInfo) {
        return <Loader show={true} bg={undefined} size={undefined}/>
    }

    return <>
        <Slider showCloseButton={false}
            placement={'bottom'}
            show={open === 'main'}
            onClose={handleCloseCommon}
            style={sliderStyle}
        >
            {
                !!closingPosition && <Loader show={true} bg={undefined} size={undefined}/>
            }
            <div className="d-flex flex-column">
                <SlideMobileModalHeaderStyled>
                    <div className="close" onClick={handleCloseCommon}>
                        <BinIcon name={'IcClose'} size={24} style={{
                            fill: 'rgba(255, 255, 255, 0.6)'
                        }}/>
                    </div>
                </SlideMobileModalHeaderStyled>
                <SlideMobileModalBoxStyled>
                    <SlideMobileAssetInfoStyled onClick={clickAssetHandler}>
                        <SlideMobileAssetInfoSymbolStyled>
                            <IconAsset symbol={positionInfo.symbol} size={'40px'}/>
                        </SlideMobileAssetInfoSymbolStyled>
                        <div className="d-flex flex-column flex-1">
                            <div className="f16">{asset?.info?.description}</div>
                            <div className={'fw600 text-secondary lh24'}>
                                <LiteTradePortfolioPositionInfo
                                    hideOpenPrice
                                    item={{
                                        ...positionInfo,
                                        assetInstance,
                                    }}
                                />
                            </div>
                        </div>
                        <BinIcon name={'IcMoveRight'}/>
                    </SlideMobileAssetInfoStyled>
                    <SlideMobilePositionModalMarket
                        symbol={symbol}
                        positionCode={positionCode}
                        assetInstance={assetInstance}
                    />
                    <SlideMobileModalInfoRowStyled>
                        <div className="label">{getLangPath('lite_version.order.open_price')}</div>
                        <div className="price">{positionInfo.openPrice}</div>
                    </SlideMobileModalInfoRowStyled>
                    {
                        !!orderStopLoss && <SlideMobileModalInfoRowStyled>
                            <div className="label">{getLangPath('lite_version.order.stop_loss')}</div>
                            <div className="price">{assetInstance.amountToFixed(orderStopLoss.legs[0].price)}</div>
                        </SlideMobileModalInfoRowStyled>
                    }
                    {
                        !!orderTakeProfit && <SlideMobileModalInfoRowStyled>
                            <div className="label">{getLangPath('lite_version.order.take_profit')}</div>
                            <div className="price">{assetInstance.amountToFixed(orderTakeProfit.legs[0].price)}</div>
                        </SlideMobileModalInfoRowStyled>
                    }
                </SlideMobileModalBoxStyled>
                <MobilePositionInactiveWrapper
                    timeToBidding={timeToBidding}
                    clickAssetScheduleHandler={clickAssetScheduleHandler}
                >
                    <div className="d-flex flex-column mt-3">
                        <Button variant="secondary" onClick={editPositionHandler}>
                            {getLangPath('lite_version.portfolio.edit_position')}
                        </Button>
                    </div>

                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="tertiary" onClick={closePositionHandler}>
                            {getLangPath('lite_version.portfolio.close_position')}
                        </Button>
                    </div>
                </MobilePositionInactiveWrapper>
            </div>
        </Slider>
        <Slider showCloseButton={false}
            placement={'bottom'}
            show={open === 'last_position'}
            onClose={handleCloseCommon}
            style={sliderNoticeStyle}
        >
            <SlideMobilePositionModalLastContainerStyled>
                <BinIcon name={'IcAttention'}
                    style={square40Style}
                    fill='var(--color-negative-secondary)'
                />
                <div dangerouslySetInnerHTML={{ __html: getLangPath('lite_version.portfolio.close_position_notice') }}/>
            </SlideMobilePositionModalLastContainerStyled>
            <div className="d-flex flex-column mt-3">
                <Button variant="secondary" onClick={closePositionHandler} style={redColorStyle}>
                    {getLangPath('lite_version.portfolio.close_position_anyway')}
                </Button>
            </div>
            <div className="d-flex flex-column mt-2">
                <Button variant={'secondary'} onClick={onTopUpClickHandler}>
                    <BinIcon name={'IcPlusCircle'}/> {getLangPath('lite_version.home.top_up')}
                </Button>
            </div>
        </Slider>
    </>;
}

const sliderStyle = {
    backgroundColor: 'var(--color-background-secondary-to-primary)',
};

const sliderNoticeStyle = {
    ...sliderStyle,
    margin: 16,
    marginBottom: 20,
    width: 'auto',
    borderRadius: 16
};

const redColorStyle = { color: 'var(--color-negative-secondary)' };

const square40Style = { width: 40, height: 40 };

export default memo(SlideMobilePositionModal);
